import React, { useState, useEffect, useRef } from 'react';
import { useWallet } from '../context/WalletContext';
import { getWalletProvider, isSolanaWallet } from '../utils/walletUtils';
import * as api from '../services/api';
import '../styles/node-application-updated.css';

// Country list for dropdown
const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia",
  "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium",
  "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria",
  "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Central African Republic", "Chad",
  "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic",
  "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea",
  "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany",
  "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras", "Hungary",
  "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan", "Jordan",
  "Kazakhstan", "Kenya", "Kiribati", "Korea, North", "Korea, South", "Kosovo", "Kuwait", "Kyrgyzstan", "Laos",
  "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar",
  "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico",
  "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia",
  "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Macedonia", "Norway",
  "Oman", "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines",
  "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia",
  "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal",
  "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia",
  "South Africa", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria",
  "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia",
  "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States",
  "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
];

// Team size ranges
const TEAM_SIZE_RANGES = [
  { label: "1-10 members", value: "1-10" },
  { label: "11-50 members", value: "11-50" },
  { label: "51-100 members", value: "51-100" },
  { label: "101-500 members", value: "101-500" },
  { label: "500+ members", value: "500+" }
];

/**
 * Check if an address is valid (either Ethereum or Solana)
 * @param {string} address - The wallet address to validate
 * @returns {boolean} Whether the address is valid
 */
const isValidWalletAddress = (address) => {
  if (!address || typeof address !== 'string') return false;
  
  // Ethereum address validation (0x prefix, 42 chars total, hex characters)
  const isEthAddress = /^0x[a-fA-F0-9]{40}$/.test(address);
  
  // Solana address validation - more permissive regex that matches actual Solana addresses
  // Solana addresses are base58 encoded and typically 32-44 characters in length
  const isSolAddress = !address.startsWith('0x') && 
                      /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(address);
  
  return isEthAddress || isSolAddress;
};


/**
 * Convert a byte array or Uint8Array to a Base64 string
 * @param {Uint8Array|Array} bytes - The byte array to convert
 * @returns {string} The Base64 encoded string
 */
const bytesToBase64 = (bytes) => {
  // If it's an array-like object but not a Uint8Array, convert it
  if (bytes && typeof bytes === 'object' && !bytes.buffer && Object.keys(bytes).length > 0) {
    bytes = Uint8Array.from(Object.values(bytes));
  }
  
  // Handle different types of input
  let binary = '';
  if (bytes instanceof Uint8Array) {
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
  } else if (Array.isArray(bytes)) {
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
  } else if (typeof bytes === 'string') {
    // Already a string, just return it
    return bytes;
  } else {
    console.error('Unknown signature format:', bytes);
    return '';
  }
  
  return btoa(binary);
};

// Function to extract numeric value from staking requirement string
const getNumericStakingRequirement = (stakingReq) => {
  if (!stakingReq) return 0;
  
  // Extract digits and remove commas
  const numericPart = stakingReq.toString().replace(/,/g, '').match(/\d+/);
  return numericPart ? parseInt(numericPart[0]) : 0;
};

const NodeApplicationForm = ({ selectedTier, nodeTiers, selectedAfiValue }) => {
  const { wallet, openWalletModal } = useWallet();
  const countryRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    walletAddress: wallet.address || '',
    telegram: '',
    country: '',
    experience: 'beginner', // Default to beginner
    teamSize: '1-10', // Default team size range
    currentAFI: '',
    strategy: '',
    referrer: '',
    agreeToTerms: false
  });
  
  // Country search
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  
  // Component state
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const [submissionErrorDetails, setSubmissionErrorDetails] = useState({});
  const [applicationResult, setApplicationResult] = useState(null);
  const [blockchainNetworks, setBlockchainNetworks] = useState([]);
  const [walletTypes, setWalletTypes] = useState([]);
  const [selectedBlockchainNetwork, setSelectedBlockchainNetwork] = useState(1); // Default to Ethereum
  const [signatureMessage, setSignatureMessage] = useState('');
  const [signature, setSignature] = useState('');
  
  const selectedTierData = nodeTiers.find(tier => tier.id === selectedTier);

  // Update AFI holdings when selectedTier or selectedAfiValue changes
  useEffect(() => {
    // First check if we have a direct AFI value passed from the tier selection
    if (selectedAfiValue) {
      setFormData(prevData => ({
        ...prevData,
        currentAFI: selectedAfiValue.toString()
      }));
      console.log(`Updated AFI directly from selection: ${selectedAfiValue}`);
    } 
    // Otherwise, extract it from the tier data
    else if (selectedTierData) {
      const stakingRequirement = selectedTierData.stakingRequirement;
      
      if (stakingRequirement) {
        const afiValue = getNumericStakingRequirement(stakingRequirement);
        
        setFormData(prevData => ({
          ...prevData,
          currentAFI: afiValue.toString()
        }));
        console.log(`Updated AFI from tier data: ${afiValue}`);
      }
    }
  }, [selectedTier, selectedTierData, selectedAfiValue]);

  // Close country dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryRef.current && !countryRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch blockchain networks and wallet types when component mounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch blockchain networks
        const networks = await api.fetchBlockchainNetworks();
        setBlockchainNetworks(networks);
        if (networks.length > 0) {
          setSelectedBlockchainNetwork(networks[0].id);
        }
        
        // Fetch wallet types
        const types = await api.fetchWalletTypes();
        setWalletTypes(types);
      } catch (error) {
        console.error('Failed to fetch form data:', error);
      }
    };
    
    fetchData();
  }, []);
  
  // Update wallet address when wallet connection changes
  useEffect(() => {
    if (wallet.address) {
      setFormData(prevData => ({
        ...prevData,
        walletAddress: wallet.address
      }));
    }
  }, [wallet.address]);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  // Handle country search and selection
  const handleCountrySearch = (e) => {
    setCountrySearch(e.target.value);
    setShowCountryDropdown(true);
  };
  
  const handleCountrySelect = (country) => {
    setFormData(prevData => ({
      ...prevData,
      country
    }));
    setCountrySearch('');
    setShowCountryDropdown(false);
    
    // Clear country error if exists
    if (formErrors.country) {
      setFormErrors(prev => ({ ...prev, country: '' }));
    }
  };
  
  // Filter countries based on search
  const filteredCountries = COUNTRIES.filter(
    country => country.toLowerCase().includes(countrySearch.toLowerCase())
  ).slice(0, 10); // Limit to first 10 matches for performance
  
  const validateForm = () => {
    const errors = {};
    
    if (!formData.fullName.trim()) errors.fullName = 'Full name is required';
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email address is invalid';
    }
    
    if (!formData.walletAddress.trim()) {
      errors.walletAddress = 'Wallet address is required';
    } else if (!isValidWalletAddress(formData.walletAddress)) {
      errors.walletAddress = 'Please enter a valid Ethereum or Solana wallet address';
    }
    
    if (!formData.country.trim()) errors.country = 'Country is required';
    
    if (!formData.teamSize) {
      errors.teamSize = 'Team size is required';
    }
    
    if (!formData.currentAFI) {
      errors.currentAFI = 'Current AFI holdings is required';
    } else if (
      selectedTierData && 
      parseInt(formData.currentAFI.toString().replace(/,/g, '')) < 
      getNumericStakingRequirement(selectedTierData.stakingRequirement)
    ) {
      errors.currentAFI = `Must have at least ${selectedTierData.stakingRequirement} for this tier`;
    }
    
    if (!formData.strategy.trim()) errors.strategy = 'Please provide your strategy';
    
    if (!formData.agreeToTerms) errors.agreeToTerms = 'You must agree to the terms';
    
    return errors;
  };
  
  // Convert team size range to numeric value for API submission
  const getTeamSizeValue = (range) => {
    switch (range) {
      case '1-10': return 5; // Average of range
      case '11-50': return 30;
      case '51-100': return 75;
      case '101-500': return 250;
      case '500+': return 500;
      default: return 5;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      // Scroll to the first error
      const firstErrorField = document.querySelector('.error');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    
    setIsSubmitting(true);
    setSubmissionError('');
    setSubmissionErrorDetails({});
    
    try {
      // Direct implementation that matches the server requirements
      console.log("Starting wallet signature process...");
      
      // Step 1: Generate message from API
      const msgResponse = await fetch('https://api.aeronyx.network/api/generate-signature-message/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wallet_address: formData.walletAddress,
          blockchain_network_id: selectedBlockchainNetwork
        }),
      });
      
      const msgData = await msgResponse.json();
      if (!msgData.success) {
        throw new Error(msgData.message || "Failed to generate signature message");
      }
      
      const { message, timestamp } = msgData.data;
      console.log("Message to sign:", message);
      
      // Step 2: Sign with wallet
      const provider = getWalletProvider(wallet.type);
      if (!provider) {
        throw new Error(`${wallet.type} wallet provider not found`);
      }
      
      // Ensure wallet is enabled
      if (provider.enable) {
        await provider.enable();
      }

      // Check if it's a Solana wallet or address
      const isSolana = isSolanaWallet(formData.walletAddress);
      
      // Use the specific provider and method for signing
      let signature;

      if (isSolana) {
        try {
          const encodedMessage = new TextEncoder().encode(message);
          let signResult;
          
          if (wallet.type.toLowerCase() === 'phantom' || wallet.type.toLowerCase() === 'solflare') {
            signResult = await provider.signMessage(encodedMessage, 'utf8');
          } else if (wallet.type.toLowerCase() === 'okx') {
            const solanaProvider = window.okxwallet?.solana;
            if (!solanaProvider) {
              throw new Error('OKX Solana provider not available');
            }
            signResult = await solanaProvider.signMessage(encodedMessage, 'utf8');
          } else {
            throw new Error(`Unsupported Solana wallet type: ${wallet.type}`);
          }
          
          // Convert signature to Base64 string consistently
          signature = bytesToBase64(signResult.signature || signResult);
          
          console.log("Generated signature:", signature);
        } catch (error) {
          console.error("Signature generation failed:", error);
          throw error; // Re-throw to allow caller to handle the error
        }
      } else {
  // 处理EVM钱包签名 (Ethereum, BSC等)
      try {
        console.log("Generating EVM signature for message:", message);
    
    // 使用EVM钱包的personal_sign方法
        signature = await provider.request({
          method: 'personal_sign',
          params: [message, formData.walletAddress]
        });
    
        console.log("Generated EVM signature:", signature);
    
        if (!signature) {
          throw new Error('Failed to generate signature');
        }
      } catch (error) {
        console.error("EVM Signature generation failed:", error);
        throw error;
      }
    }
      
      // Step 3: Submit application
      const applicationData = {
        full_name: formData.fullName,
        email: formData.email,
        telegram: formData.telegram || '',
        country: formData.country,
        blockchain_network_id: selectedBlockchainNetwork,
        wallet_address: formData.walletAddress,
        wallet_type: wallet.type,
        signature: signature,
        signature_message: message,
        signature_timestamp: timestamp,
        tier_id: selectedTier,
        experience: formData.experience || 'beginner',
        team_size: getTeamSizeValue(formData.teamSize),
        current_afi: formData.currentAFI.toString().replace(/,/g, ''),
        strategy: formData.strategy,
        referrer: formData.referrer || '',
        agrees_to_terms: formData.agreeToTerms
      };
      
      console.log("Submitting application with data:", applicationData);
      
      const response = await fetch('https://api.aeronyx.network/api/node-applications/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(applicationData),
      });
      
      const result = await response.json();
      console.log("Submission result:", result);
      
      if (result.success) {
        setApplicationResult(result.data);
        setIsSubmitted(true);
        
        // Scroll to top of form for success message
        document.getElementById('application-form').scrollIntoView({ behavior: 'smooth' });
      } else {
        setSubmissionError(result.message || "Application submission failed");
        setSubmissionErrorDetails(result.errors || {});
      }
    } catch (error) {
      console.error("Error in application process:", error);
      setSubmissionError(error.message || "Failed to process application");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Check if wallet is connected
  if (!wallet.connected) {
    return (
      <div className="wallet-connection-required">
        <p>Please connect your wallet to apply for node operator status.</p>
        <button 
          className="btn" 
          onClick={openWalletModal}
        >
          Connect Wallet
        </button>
      </div>
    );
  }
  
  if (isSubmitted && applicationResult) {
    return (
      <div className="form-success-message">
        <div className="success-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18457 2.99721 7.13633 4.39828 5.49707C5.79935 3.85782 7.69279 2.71538 9.79619 2.24015C11.8996 1.76491 14.1003 1.98234 16.07 2.86" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M22 4L12 14.01L9 11.01" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <h3>Application Submitted Successfully!</h3>
        <p>Thank you for applying to become a {selectedTierData ? selectedTierData.name : 'Node Operator'}. Our team will review your application and get back to you within 48 hours via the email address you provided.</p>
        <p className="application-ref">Application Reference: {applicationResult.application.reference_code}</p>
        
        {applicationResult.next_steps && applicationResult.next_steps.length > 0 && (
          <div className="next-steps-container">
            <h4>Next Steps:</h4>
            <ul className="next-steps-list">
              {applicationResult.next_steps.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ul>
          </div>
        )}
        
        <button 
          className="btn" 
          onClick={() => {
            setIsSubmitted(false);
            setFormData({
              fullName: '',
              email: '',
              walletAddress: wallet.address || '',
              telegram: '',
              country: '',
              experience: 'beginner',
              teamSize: '1-10',
              currentAFI: selectedTierData ? getNumericStakingRequirement(selectedTierData.stakingRequirement).toString() : '',
              strategy: '',
              referrer: '',
              agreeToTerms: false
            });
            setApplicationResult(null);
          }}
        >
          Submit Another Application
        </button>
      </div>
    );
  }
  
  if (!selectedTier) {
    return (
      <div className="no-tier-selected">
        <p>Please select a node tier from above to continue with your application.</p>
      </div>
    );
  }

  return (
    <form className="node-application-form" onSubmit={handleSubmit}>
      {/* Selected Tier Summary */}
      <div className="selected-tier-summary">
        <h4>Applying for: {selectedTierData?.name}</h4>
        <p>
          Staking Requirement: <span>{selectedTierData?.stakingRequirement}</span> | 
          Lock Period: <span>{selectedTierData?.lock_period || selectedTierData?.lockPeriod}</span> | 
          Max Devices: <span>{selectedTierData?.max_devices || selectedTierData?.maxDevices}</span>
        </p>
      </div>
      
      {/* Submission Error */}
      {submissionError && (
        <div className="submission-error">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/>
            <path d="M12 8V12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
            <path d="M12 16H12.01" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <p>{submissionError}</p>
          
          {/* Display detailed field errors from API if available */}
          {Object.keys(submissionErrorDetails).length > 0 && (
            <ul className="error-details-list">
              {Object.entries(submissionErrorDetails).map(([field, errors]) => (
                <li key={field}>
                  <strong>{field}:</strong> {Array.isArray(errors) ? errors.join(', ') : errors}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      
      {/* Form Fields */}
      <div className="form-grid">
        <div className="form-group">
          <label htmlFor="fullName">Full Name *</label>
          <input
            type="text"
            id="fullName"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            className={formErrors.fullName || submissionErrorDetails.full_name ? 'error' : ''}
            placeholder="Enter your full name"
          />
          {formErrors.fullName && <div className="error-message">{formErrors.fullName}</div>}
          {submissionErrorDetails.full_name && <div className="error-message">{submissionErrorDetails.full_name}</div>}
        </div>
        
        <div className="form-group">
          <label htmlFor="email">Email Address *</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={formErrors.email || submissionErrorDetails.email ? 'error' : ''}
            placeholder="you@example.com"
          />
          {formErrors.email && <div className="error-message">{formErrors.email}</div>}
          {submissionErrorDetails.email && <div className="error-message">{submissionErrorDetails.email}</div>}
        </div>
        
        <div className="form-group">
          <label htmlFor="walletAddress">Wallet Address *</label>
          <input
            type="text"
            id="walletAddress"
            name="walletAddress"
            value={formData.walletAddress}
            className={`wallet-address-field ${formErrors.walletAddress || submissionErrorDetails.wallet_address ? 'error' : ''}`}
            readOnly
          />
          {formErrors.walletAddress && <div className="error-message">{formErrors.walletAddress}</div>}
          {submissionErrorDetails.wallet_address && <div className="error-message">{submissionErrorDetails.wallet_address}</div>}
          <div className="field-note">This is the connected wallet that will be used for staking</div>
        </div>
        
        <div className="form-group">
          <label htmlFor="telegram">Telegram Username (Optional)</label>
          <input
            type="text"
            id="telegram"
            name="telegram"
            value={formData.telegram}
            onChange={handleChange}
            className={submissionErrorDetails.telegram ? 'error' : ''}
            placeholder="@yourusername"
          />
          {submissionErrorDetails.telegram && <div className="error-message">{submissionErrorDetails.telegram}</div>}
        </div>
        
        <div className="form-group" ref={countryRef}>
          <label htmlFor="country">Country *</label>
          <div className="country-select-container">
            <input
              type="text"
              id="country"
              value={formData.country || countrySearch}
              onChange={handleCountrySearch}
              onFocus={() => setShowCountryDropdown(true)}
              placeholder="Type to search countries"
              className={formErrors.country || submissionErrorDetails.country ? 'error' : ''}
            />
            {showCountryDropdown && (
              <div className="country-dropdown" style={{ 
                backgroundColor: 'var(--dark)',
                color: 'var(--text)',
                borderColor: 'var(--glass-border)'
              }}>
                {filteredCountries.length === 0 ? (
                  <div className="no-results">No countries found</div>
                ) : (
                  filteredCountries.map(country => (
                    <div 
                      key={country} 
                      className="country-option"
                      onClick={() => handleCountrySelect(country)}
                      style={{
                        borderBottomColor: 'var(--glass-border)',
                        color: 'var(--text)',
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.1)'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      {country}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          {formErrors.country && <div className="error-message">{formErrors.country}</div>}
          {submissionErrorDetails.country && <div className="error-message">{submissionErrorDetails.country}</div>}
        </div>
        
        <div className="form-group">
          <label htmlFor="experience">Experience Level</label>
          <select
            id="experience"
            name="experience"
            value={formData.experience}
            onChange={handleChange}
            className={submissionErrorDetails.experience ? 'error' : ''}
          >
            <option value="beginner">Beginner - New to blockchain</option>
            <option value="intermediate">Intermediate - Some blockchain experience</option>
            <option value="advanced">Advanced - Extensive blockchain experience</option>
            <option value="expert">Expert - Professional blockchain experience</option>
          </select>
          {submissionErrorDetails.experience && <div className="error-message">{submissionErrorDetails.experience}</div>}
        </div>
        
        <div className="form-group">
          <label htmlFor="teamSize">Estimated Initial Team Size *</label>
          <select
            id="teamSize"
            name="teamSize"
            value={formData.teamSize}
            onChange={handleChange}
            className={formErrors.teamSize || submissionErrorDetails.team_size ? 'error' : ''}
          >
            {TEAM_SIZE_RANGES.map((range) => (
              <option key={range.value} value={range.value}>{range.label}</option>
            ))}
          </select>
          {formErrors.teamSize && <div className="error-message">{formErrors.teamSize}</div>}
          {submissionErrorDetails.team_size && <div className="error-message">{submissionErrorDetails.team_size}</div>}
        </div>
        
        <div className="form-group">
          <label htmlFor="currentAFI">Current AFI Holdings *</label>
          <div className="afi-input-container">
            <input
              type="text"
              id="currentAFI"
              name="currentAFI"
              value={formData.currentAFI}
              className={formErrors.currentAFI || submissionErrorDetails.current_afi ? 'error' : ''}
              readOnly
            />
            <span className="afi-suffix">AFI</span>
          </div>
          <div className="field-note">
            Staking requirement for selected tier. You must have this amount of AFI available.
          </div>
          {formErrors.currentAFI && <div className="error-message">{formErrors.currentAFI}</div>}
          {submissionErrorDetails.current_afi && <div className="error-message">{submissionErrorDetails.current_afi}</div>}
        </div>
        
        <div className="form-group full-width">
          <label htmlFor="strategy">Briefly describe your strategy for growing your node team *</label>
          <textarea
            id="strategy"
            name="strategy"
            value={formData.strategy}
            onChange={handleChange}
            rows="4"
            className={formErrors.strategy || submissionErrorDetails.strategy ? 'error' : ''}
            placeholder="Describe how you plan to recruit team members, your marketing approach, and your goals for network growth..."
          ></textarea>
          {formErrors.strategy && <div className="error-message">{formErrors.strategy}</div>}
          {submissionErrorDetails.strategy && <div className="error-message">{submissionErrorDetails.strategy}</div>}
        </div>
        
        <div className="form-group">
          <label htmlFor="referrer">Referrer Code (Optional)</label>
          <input
            type="text"
            id="referrer"
            name="referrer"
            value={formData.referrer}
            onChange={handleChange}
            className={submissionErrorDetails.referrer ? 'error' : ''}
            placeholder="ALD-12345 or wallet address"
          />
          {submissionErrorDetails.referrer && <div className="error-message">{submissionErrorDetails.referrer}</div>}
        </div>
        
        <div className="form-group full-width">
          <label className="checkbox-container">
            <input
              type="checkbox"
              name="agreeToTerms"
              checked={formData.agreeToTerms}
              onChange={handleChange}
            />
            I agree to the <a href="https://docs.aeronyx.network/articles/aledger-node-application-terms" target="_blank" rel="noopener noreferrer" className="terms-link">Terms and Conditions</a> for node operators
          </label>
          {formErrors.agreeToTerms && <div className="error-message">{formErrors.agreeToTerms}</div>}
          {submissionErrorDetails.agrees_to_terms && <div className="error-message">{submissionErrorDetails.agrees_to_terms}</div>}
        </div>
      </div>
      
      <div className="form-action">
        <div className="required-fields">
          <span>*</span> Required fields
        </div>
        <button 
          type="submit" 
          className="btn submit-btn" 
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="spinner"></span>
              Processing...
            </>
          ) : (
            'Submit Application'
          )}
        </button>
      </div>
    </form>
  );
};

export default NodeApplicationForm;
